import { useEffect, useRef, useState } from "react";
import { Button, Card, Input, Radio } from "@omnichannel/ui";
import { ApiError, request } from "./api";
import { money } from "./money";
export { money } from "./money";
import { AIRefundPolicy } from "./AIRefundPolicy";
import { RefundReconciliation } from "./RefundReconciliation";
import {
  AICompensations,
  type Compensation,
  type RevenueAdjustment,
} from "./AICompensations";
interface Person {
  id: string;
  email: string | null;
}
interface Product {
  app_id: string;
  app_name: string;
  external_customer_id: string;
  external_customer_ids?: string[];
  events: {
    id: string;
    event_type: string;
    tariff: string | null;
    amount: number | null;
    currency: string | null;
    renews_at: string | null;
    occurred_at: string;
  }[];
  support: { id: string; subject: string; is_open: boolean }[];
}
interface Detail extends Person {
  products: Product[];
}
interface Pricing {
  override: {
    mode: string;
    value: number;
    expires_at: string | null;
    reason: string;
    is_active: boolean;
  } | null;
  audit: {
    id: string;
    action: string;
    mode: string | null;
    value: number | null;
    expires_at: string | null;
    reason: string;
    actor_id: string;
    occurred_at: string;
  }[];
}
interface Rule {
  min_products: number;
  discount_percent: number;
}
interface Wallet {
  compensations?: Compensation[];
  revenue_adjustments?: RevenueAdjustment[];
  balance: number;
  currency: string;
  ledger: {
    id: string;
    kind: string;
    amount: number;
    balance_after: number;
    fx_rate: string | null;
    ref: string | null;
    occurred_at: string;
  }[];
  tasks: {
    id: string;
    app_id: string;
    task_ref: string;
    status: string;
    reserved: number;
    charge: number | null;
    provider_cost_usd: string | null;
    fx_rate: string | null;
    buffer_percent: string | null;
    created_at: string;
  }[];
}
const errorText = (e: unknown) =>
  e instanceof ApiError
    ? e.message
    : "Не удалось выполнить запрос. Попробуйте снова.";
const date = (v: string | null) =>
  v ? new Date(v).toLocaleString("ru-RU") : "—";
function groupProducts(products: Product[]): Product[] {
  const grouped = new Map<string, Product>();
  for (const product of products) {
    const previous = grouped.get(product.app_id);
    const aliases = [
      ...new Set([
        ...(previous?.external_customer_ids || []),
        product.external_customer_id,
      ]),
    ];
    const events = [
      ...new Map(
        [...(previous?.events || []), ...product.events].map((event) => [
          event.id,
          event,
        ]),
      ).values(),
    ];
    events.sort(
      (a, b) =>
        new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
    );
    const support = [
      ...new Map(
        [...(previous?.support || []), ...product.support].map((thread) => [
          thread.id,
          thread,
        ]),
      ).values(),
    ];
    grouped.set(product.app_id, {
      ...product,
      external_customer_ids: aliases,
      events,
      support,
    });
  }
  return [...grouped.values()];
}
export function OverrideEditor({
  personId,
  product,
}: {
  personId: string;
  product: Pick<Product, "app_id" | "app_name">;
}) {
  const path = `/api/v1/persons/${personId}/pricing-overrides/${product.app_id}`;
  const [pricing, setPricing] = useState<Pricing | null>(null),
    [mode, setMode] = useState("discount_percent"),
    [value, setValue] = useState(""),
    [expiry, setExpiry] = useState(""),
    [reason, setReason] = useState(""),
    [error, setError] = useState(""),
    [pending, setPending] = useState(false),
    [now, setNow] = useState(Date.now());
  const load = async () => {
    try {
      setPricing(await request<Pricing>(path));
      setError("");
    } catch (e) {
      setError(errorText(e));
    }
  };
  useEffect(() => {
    void load();
  }, [path]);
  const expiresAt = pricing?.override?.expires_at;
  useEffect(() => {
    if (!expiresAt) return;
    const remaining = new Date(expiresAt).getTime() - Date.now();
    if (remaining <= 0) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(remaining + 1, 2147483647),
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt, now]);
  const expired = !!expiresAt && new Date(expiresAt).getTime() <= Date.now();
  const effectiveOverride = !!pricing?.override?.is_active && !expired;
  const attempt = useRef<{ signature: string; key: string } | null>(null);
  const change = async (remove: boolean) => {
    setError("");
    if (!reason.trim()) {
      setError("Укажите причину изменения цены");
      return;
    }
    const amount = Number(value);
    if (
      !remove &&
      mode !== "free" &&
      (!/^\d+$/.test(value) ||
        !Number.isSafeInteger(amount) ||
        (mode === "discount_percent" && amount > 100))
    ) {
      setError("Введите целое неотрицательное значение; скидка не выше 100%");
      return;
    }
    setPending(true);
    try {
      const payload = remove
        ? { reason: reason.trim() }
        : {
            mode,
            value: mode === "free" ? 0 : amount,
            expires_at: expiry ? new Date(expiry).toISOString() : null,
            reason: reason.trim(),
          };
      const signature = JSON.stringify({ remove, payload });
      if (attempt.current?.signature !== signature) {
        attempt.current = { signature, key: crypto.randomUUID() };
      }
      await request(path, {
        method: remove ? "DELETE" : "PUT",
        body: JSON.stringify({
          ...payload,
          idempotency_key: attempt.current.key,
        }),
      });
      attempt.current = null;
      await load();
      setReason("");
    } catch (e) {
      setError(errorText(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <section aria-label={`Цена ${product.app_name}`}>
      <h3>Индивидуальная цена</h3>
      {pricing ? (
        <p>
          <span>
            {effectiveOverride && pricing.override
              ? `${pricing.override.mode}: ${pricing.override.value}; до ${date(pricing.override.expires_at)}`
              : expired && pricing.override?.is_active
                ? "Срок индивидуальной цены истёк · Обычная цена"
                : "Обычная цена"}
          </span>{" "}
          · {pricing.override?.reason}
        </p>
      ) : (
        <p role="status">Загрузка цены…</p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void change(false);
        }}
      >
        <fieldset disabled={pending}>
          <legend>Режим</legend>
          {[
            ["discount_percent", "Скидка %"],
            ["fixed_price", "Фиксированная цена"],
            ["free", "Бесплатно"],
          ].map(([v, l]) => (
            <Radio
              key={v}
              name={`mode-${product.app_id}`}
              label={l}
              checked={mode === v}
              onChange={() => setMode(v)}
            />
          ))}
        </fieldset>
        <div className="person-form">
          {mode !== "free" && (
            <label>
              {mode === "fixed_price"
                ? "Цена в минимальных единицах валюты тарифа"
                : "Процент скидки"}
              <Input
                type="number"
                min="0"
                step="1"
                max={mode === "discount_percent" ? 100 : undefined}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
                disabled={pending}
              />
            </label>
          )}
          <label>
            Действует до (местное время)
            <Input
              type="datetime-local"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              disabled={pending}
            />
          </label>
          <label>
            Причина
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              disabled={pending}
            />
          </label>
          <Button type="submit" disabled={pending}>
            Сохранить цену
          </Button>
          <Button
            variant="secondary"
            disabled={pending || !effectiveOverride}
            onClick={() => void change(true)}
          >
            Вернуть цену
          </Button>
        </div>
      </form>
      {error && (
        <p role="alert">
          {error}{" "}
          <Button variant="ghost" onClick={() => void load()}>
            Повторить загрузку цены
          </Button>
        </p>
      )}
      <details>
        <summary>Аудит изменения цены</summary>
        {pricing?.audit.map((a) => (
          <p key={a.id}>
            {date(a.occurred_at)} · {a.action} · {a.mode} {a.value} · {a.reason}{" "}
            · {a.actor_id} · Срок:{" "}
            {a.expires_at ? date(a.expires_at) : "Без срока"}
          </p>
        ))}
      </details>
    </section>
  );
}
export function Rules() {
  const [rules, setRules] = useState<Rule[]>([]),
    [error, setError] = useState(""),
    [pending, setPending] = useState(false),
    [loaded, setLoaded] = useState(false);
  const load = async () => {
    try {
      setRules(await request<Rule[]>("/api/v1/pricing/multiproduct-rules"));
      setLoaded(true);
      setError("");
    } catch (e) {
      setError(errorText(e));
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const save = async () => {
    setPending(true);
    setError("");
    try {
      setRules(
        await request<Rule[]>("/api/v1/pricing/multiproduct-rules", {
          method: "PUT",
          body: JSON.stringify({ rules }),
        }),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <Card className="person-section">
      <h2>Мультипродуктовые скидки</h2>
      <p>Количество активных продуктов → процент автоматической скидки.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {rules.map((r, i) => (
          <div className="person-form" key={i}>
            <label>
              Продуктов от
              <Input
                type="number"
                min="1"
                step="1"
                required
                value={r.min_products}
                disabled={pending}
                onChange={(e) =>
                  setRules(
                    rules.map((v, j) =>
                      j === i
                        ? { ...v, min_products: Number(e.target.value) }
                        : v,
                    ),
                  )
                }
              />
            </label>
            <label>
              Скидка, %
              <Input
                type="number"
                min="0"
                max="100"
                step="1"
                required
                value={r.discount_percent}
                disabled={pending}
                onChange={(e) =>
                  setRules(
                    rules.map((v, j) =>
                      j === i
                        ? { ...v, discount_percent: Number(e.target.value) }
                        : v,
                    ),
                  )
                }
              />
            </label>
            <Button
              variant="ghost"
              disabled={pending}
              onClick={() => setRules(rules.filter((_, j) => j !== i))}
            >
              Убрать правило {i + 1}
            </Button>
          </div>
        ))}
        <Button
          variant="secondary"
          disabled={pending || !loaded}
          onClick={() =>
            setRules([
              ...rules,
              {
                min_products: rules.length
                  ? Math.max(...rules.map((r) => r.min_products)) + 1
                  : 2,
                discount_percent: 10,
              },
            ])
          }
        >
          Добавить правило
        </Button>{" "}
        <Button type="submit" disabled={pending || !loaded}>
          Сохранить правила
        </Button>
      </form>
      {error && (
        <p role="alert">
          {error}{" "}
          <Button variant="ghost" onClick={() => void load()}>
            Повторить загрузку правил
          </Button>
        </p>
      )}
    </Card>
  );
}
function PersonDetail({ id }: { id: string }) {
  const [detail, setDetail] = useState<Detail | null>(null),
    [wallet, setWallet] = useState<Wallet | null>(null),
    [error, setError] = useState(""),
    [walletError, setWalletError] = useState(""),
    [walletPending, setWalletPending] = useState(false);
  const detailGeneration = useRef(0);
  const walletGeneration = useRef(0);
  const loadDetail = async () => {
    const g = ++detailGeneration.current;
    setError("");
    setDetail(null);
    try {
      const d = await request<Detail>(`/api/v1/persons/${id}`);
      if (g === detailGeneration.current) setDetail(d);
    } catch (e) {
      if (g === detailGeneration.current) setError(errorText(e));
    }
  };
  const loadWallet = async () => {
    const g = ++walletGeneration.current;
    setWalletError("");
    setWalletPending(true);
    try {
      const w = await request<Wallet>(`/api/v1/persons/${id}/wallet`);
      if (g === walletGeneration.current) setWallet(w);
    } catch (e) {
      if (g === walletGeneration.current) setWalletError(errorText(e));
    } finally {
      if (g === walletGeneration.current) setWalletPending(false);
    }
  };
  const load = () => {
    void loadDetail();
    void loadWallet();
  };
  useEffect(() => {
    load();
    return () => {
      detailGeneration.current++;
      walletGeneration.current++;
    };
  }, [id]);
  return (
    <div aria-live="polite">
      {error && (
        <p role="alert">
          {error}{" "}
          <Button onClick={() => void loadDetail()}>
            Повторить загрузку карточки
          </Button>
        </p>
      )}
      {!detail && !error && <p role="status">Загрузка карточки…</p>}
      {detail && (
        <>
          <h2>{detail.email || detail.id}</h2>
          <p>Person: {detail.id}</p>
          <div className="person-products">
            {groupProducts(detail.products).map((p) => (
              <Card className="person-section" key={p.app_id}>
                <h2>{p.app_name}</h2>
                <p>
                  Customer ID:{" "}
                  {(p.external_customer_ids || [p.external_customer_id]).join(
                    ", ",
                  )}
                </p>
                <h3>Подписки и платежи</h3>
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Событие</th>
                        <th>Тариф</th>
                        <th>Сумма</th>
                        <th>Продление</th>
                        <th>Дата</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.events.map((e) => (
                        <tr key={e.id}>
                          <td>{e.event_type}</td>
                          <td>{e.tariff || "—"}</td>
                          <td>
                            {e.amount !== null && e.currency
                              ? money(e.amount, e.currency)
                              : "—"}
                          </td>
                          <td>{date(e.renews_at)}</td>
                          <td>{date(e.occurred_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h3>Поддержка</h3>
                {p.support.length ? (
                  p.support.map((s) => (
                    <p key={s.id}>
                      {s.subject} · {s.is_open ? "Открыто" : "Закрыто"}
                    </p>
                  ))
                ) : (
                  <p>Обращений нет</p>
                )}
                <OverrideEditor personId={id} product={p} />
                <AIRefundPolicy appId={p.app_id} appName={p.app_name} />
              </Card>
            ))}
          </div>
          {walletPending && <p role="status">Загрузка кошелька…</p>}
          {walletError && (
            <p role="alert">
              Кошелёк: {walletError}{" "}
              <Button
                onClick={() => void loadWallet()}
                disabled={walletPending}
              >
                Повторить загрузку кошелька
              </Button>
            </p>
          )}
          {wallet && (
            <Card className="person-section">
              <h2>AI-кошелёк: {money(wallet.balance, wallet.currency)}</h2>
              <Button variant="ghost" onClick={() => void load()}>
                Обновить карточку
              </Button>
              <h3>Леджер кошелька</h3>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Операция</th>
                      <th>Сумма</th>
                      <th>Баланс после</th>
                      <th>Курс</th>
                      <th>Ссылка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallet.ledger.map((l) => (
                      <tr key={l.id}>
                        <td>{date(l.occurred_at)}</td>
                        <td>{l.kind}</td>
                        <td>{money(l.amount, wallet.currency)}</td>
                        <td>{money(l.balance_after, wallet.currency)}</td>
                        <td>{l.fx_rate || "—"}</td>
                        <td>{l.ref || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <h3>AI-usage</h3>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Задача / продукт</th>
                      <th>Статус</th>
                      <th>Резерв RUB</th>
                      <th>Списано RUB</th>
                      <th>Стоимость USD</th>
                      <th>Курс + буфер</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallet.tasks.map((t) => (
                      <tr key={t.id}>
                        <td>
                          {t.task_ref} / {t.app_id}
                        </td>
                        <td>{t.status}</td>
                        <td>{money(t.reserved, "RUB")}</td>
                        <td>
                          {t.charge === null ? "—" : money(t.charge, "RUB")}
                        </td>
                        <td>{t.provider_cost_usd || "—"}</td>
                        <td>
                          {t.fx_rate || "—"} + {t.buffer_percent || "0"}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <AICompensations
                compensations={wallet.compensations || []}
                adjustments={wallet.revenue_adjustments || []}
              />
            </Card>
          )}
          <RefundReconciliation personId={id} />
        </>
      )}
    </div>
  );
}
/**
 * Legacy embedding contract retained for existing external callers and its frozen
 * test suite. App navigation uses the bounded PersonDirectory component.
 */
export function Persons() {
  const [query, setQuery] = useState(""),
    [rows, setRows] = useState<Person[]>([]),
    [selected, setSelected] = useState<string | null>(null),
    [error, setError] = useState(""),
    [pending, setPending] = useState(false),
    [searched, setSearched] = useState(false);
  const search = async () => {
    setPending(true);
    setError("");
    setSelected(null);
    setRows([]);
    try {
      setRows(
        await request<Person[]>(
          `/api/v1/persons?query=${encodeURIComponent(query.trim())}`,
        ),
      );
      setSearched(true);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <main className="shell">
      <h1>Клиенты и биллинг</h1>
      <form
        className="person-form"
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
      >
        <label>
          Customer ID или email
          <Input
            value={query}
            required
            onChange={(e) => setQuery(e.target.value)}
            disabled={pending}
          />
        </label>
        <Button type="submit" disabled={pending}>
          Найти клиента
        </Button>
      </form>
      {pending && <p role="status">Поиск…</p>}
      {error && <p role="alert">{error}</p>}
      {searched && !pending && !rows.length && !error && (
        <p>Клиенты не найдены</p>
      )}
      {rows.map((p) => (
        <Button
          variant="secondary"
          key={p.id}
          onClick={() => setSelected(p.id)}
        >
          {p.email || p.id}
        </Button>
      ))}
      {selected && <PersonDetail key={selected} id={selected} />}
      <Rules />
    </main>
  );
}
